import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useAsyncAction } from "shared/lib/useAsyncAction"
import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  UseProductPositionActionsArgs,
  useProductPositionActions
} from "features/product-position-management/model/useProductPositionActions"
import { ProductPositionFormTabType } from "features/product-position-management/types/ProductPositionFormTabType"

import { ProjectPageProductPositionSidebarProps } from "../ProjectPageProductPositionSidebar"
import { ProjectPageProductPositionSidebarViewModel } from "../ProjectPageProductPositionSidebarView"

export function useProjectPageProductPositionSidebarViewModel({
  isDefaultEditing,
  callbacks,

  formModel,
  isLoading,
  formOptions,

  isEditing,
  enterEditing,
  exitEditing,
  sidebarActiveTab,
  setSidebarActiveTab,

  ...rest
}: ProjectPageProductPositionSidebarProps): ProjectPageProductPositionSidebarViewModel {
  const { t } = useTranslation()

  // Tracks the in-flight submit so the form can be disabled while it runs.
  // useAsyncAction passes the promise through, so the form widget can still
  // inspect `result.errors` to decide whether to leave editing mode.
  const { isLoading: isSubmitting, execute: submit } = useAsyncAction(
    formModel.onSubmit
  )

  const onEdit = isDefaultEditing ? undefined : enterEditing

  const actionsArgs: UseProductPositionActionsArgs = {
    ...callbacks,
    idPrefix: "project-sidebar",
    onEdit: formModel.onSubmit ? onEdit : undefined,
    isCopyDisabled: isLoading,
    isDeleteDisabled: isLoading,
    isDuplicateDisabled: isLoading,
    onAssignAttachments: undefined,
    onAddClarificationRequest: undefined,
    onEditConfiguration: undefined,
    onSelectInstallationReferences: undefined
  }

  const { actions: normalActions } = useProductPositionActions(actionsArgs)

  const editingActions = useMemo<Action[]>(
    () => [
      {
        id: "product-position__edit__cancel",
        "data-testid": "product-position__edit__cancel",
        onClick: exitEditing,
        icon: IconName.CLOSE_CIRCLE,
        label: t("General.Cancel")
      },
      {
        id: "product-position__edit__submit",
        "data-testid": "product-position__edit__submit",
        type: "submit",
        form: formModel.id,
        icon: IconName.CHECK_CIRCLE,
        label: t("General.Confirm"),
        disabled: isSubmitting
      }
    ],
    [exitEditing, formModel.id, isSubmitting, t]
  )

  const derivedFormModel = useMemo(
    () => ({
      ...formModel,
      onSubmit: submit,
      isEditing,
      onEditingChange: (v: boolean) => (v ? enterEditing() : exitEditing()),
      formOptions: {
        ...formOptions,
        hideProject: true,
        hideOpenProductConfigurationLinkWhileEditing: true,
        hideInstallationReferenceSelectionWhileEditing: true,
        activeTab:
          (sidebarActiveTab as ProductPositionFormTabType | null) ?? undefined,
        onTabChange: setSidebarActiveTab
      }
    }),
    [
      formModel,
      submit,
      formOptions,
      isEditing,
      enterEditing,
      exitEditing,
      sidebarActiveTab,
      setSidebarActiveTab
    ]
  )

  return {
    ...rest,
    formModel: derivedFormModel,
    actions: isEditing ? editingActions : normalActions,
    isDisabled: isSubmitting,
    isLoading
  }
}
